'use client'

import { createPortal } from 'react-dom'
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import ChartPanel from '../../charts/ChartPanel'
import { formatCompactNumber, formatNumber, formatPercent } from '../../charts/formatters'
import {
  formatOptimalPostingTimezoneLabel,
  formatSlotTooltipHeader,
  getHeatmapQuartiles,
  getHeatmapQuartilesGrid,
  getMatrixCellPostingTimeMetrics,
  getWeekdaySlotPostingTimeMetrics,
  measureOptimalPostingTooltipLayout,
  OPTIMAL_POSTING_TIER_LEGEND_LABELS,
  OPTIMAL_POSTING_PROPERTY_TIMEZONE,
  OPTIMAL_POSTING_ENGAGEMENT_SUBTITLE,
  OPTIMAL_POSTING_WIDGET_SUBTITLE,
  OPTIMAL_POSTING_WIDGET_TITLE,
} from '../utils/optimalPostingTimesUtils'
import {
  deriveDateRangePostingOpportunities,
  deriveWeekdayPostingOpportunities,
} from '../utils/optimalPostingOpportunities'
import {
  adaptPlatformSeriesToOptimalPostingTimes,
  adaptPlatformSeriesToOptimalPostingMonthlyHourly,
  expandTrimmedHourlyMatrixTo24,
} from '../utils/optimalPostingTimesAdapter'
import {
  bestPeakHourWindow,
  hourTotalsFromMatrix24,
  peakBandStyleFromHours,
  shouldShowPeakBand,
  topThreePeakSlotKeys,
} from '../utils/messageHeatmapReadability'
import {
  gridWeightedEngagementMatrix24,
  weeklyWeightedEngagementMatrix24,
} from '../utils/optimalPostingTimesReadability'
import OptimalPostingTimesPanelTitle from './OptimalPostingTimesPanelTitle'
import OptimalPostingTopOpportunities, { DAILY_OPPORTUNITY_ROW_CONFIG } from './OptimalPostingTopOpportunities'
import OptimalPostingTimesSlotTooltip from './OptimalPostingTimesSlotTooltip'
import '../../charts/chart.css'
import './MessageHeatmapWidget.css'
import './MessageVolumeDailyWidget.css'
import './OptimalPostingTimesWidget.css'

export const OPTIMAL_POSTING_TIMES_UNIFIED_CHART_ID = 'optimal_posting_times'

const MIN_POSTS_FOR_DATA = 10
const MIN_POSTS_PER_SLOT = 1

const HOURS = Array.from({ length: 24 }, (_, i) => i)
const Y_AXIS_HOURS = [22, 20, 18, 16, 14, 12, 10, 8, 6, 4, 2, 0]

const DISPLAY_DAYS = [
  { label: 'Sun', dataIndex: 6, weekend: true },
  { label: 'Mon', dataIndex: 0, weekend: false },
  { label: 'Tue', dataIndex: 1, weekend: false },
  { label: 'Wed', dataIndex: 2, weekend: false },
  { label: 'Thu', dataIndex: 3, weekend: false },
  { label: 'Fri', dataIndex: 4, weekend: false },
  { label: 'Sat', dataIndex: 5, weekend: true },
]

const TIER_LEGEND_LABELS = OPTIMAL_POSTING_TIER_LEGEND_LABELS

function emptyWeeklyData() {
  return {
    matrix: Array.from({ length: 7 }, () => Array(24).fill(0)),
    samples: Array.from({ length: 7 }, () => Array(24).fill(0)),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    totalPostsUsed: 0,
  }
}

function emptyCalendarByDay() {
  return {
    dates: [],
    matrix: [],
    samples: [],
    hours: [],
    totalPostsUsed: 0,
  }
}

function emptyMonthlyData() {
  return {
    dates: [],
    matrix: [],
    samples: [],
    hours: [],
    totalPostsUsed: 0,
  }
}

/** Tab order for chart motion when switching views (Daily → Weekly → Monthly). */
const VIEW_TAB_ORDER = { daily: 0, weekly: 1, monthly: 2 }

/** Monthly view: fat bars; cluster width = n×track + (n−1)×gap for bar/X alignment. */
const MONTHLY_BAR_LAYOUT = { trackPx: 58, gapPx: 16, colWidthPx: 34 }

function formatAxisHour(h) {
  const x = Math.max(0, Math.min(23, Math.floor(h)))
  const isPM = x >= 12
  const d = x === 0 ? 12 : x > 12 ? x - 12 : x
  return `${d}:00${isPM ? 'pm' : 'am'}`
}

function getActivityLevel(val, slotSamples, quartiles) {
  if (!slotSamples || slotSamples < MIN_POSTS_PER_SLOT) return 0
  const v = val ?? 0
  if (quartiles.q1 <= 0 && quartiles.q3 <= 0) return v > 0 ? 2 : 0
  if (v <= quartiles.q1) return 1
  if (v < quartiles.q3) return 2
  return 3
}

const MIXED_TIER_COLORS = {
  1: '#E8F1FF',
  2: '#6EA8FE',
  3: '#1D4ED8',
}

const FILTER_INACTIVE_GREY = '#E5E7EB'

const FILTER_ACTIVE_BY_PINNED_TIER = {
  1: '#6EA8FE',
  2: '#6EA8FE',
  3: '#1D4ED8',
}

function getSegmentBackgroundColor(level, pinnedTier) {
  if (level === 0) return 'transparent'
  if (pinnedTier == null) return MIXED_TIER_COLORS[level] ?? 'transparent'
  if (level === pinnedTier) return FILTER_ACTIVE_BY_PINNED_TIER[pinnedTier] ?? MIXED_TIER_COLORS[level]
  return FILTER_INACTIVE_GREY
}

function getLegendSwatchColor(tierLevel, pinnedTier) {
  if (pinnedTier == null) return MIXED_TIER_COLORS[tierLevel]
  if (tierLevel === pinnedTier) return FILTER_ACTIVE_BY_PINNED_TIER[pinnedTier]
  return FILTER_INACTIVE_GREY
}

function computeTierSummaries7x24(matrix, samples, quartiles) {
  return [1, 2, 3].map((level) => {
    let slotCount = 0
    let totalPosts = 0
    let engagementWeighted = 0
    for (let d = 0; d < 7; d += 1) {
      for (let h = 0; h < 24; h += 1) {
        const val = Number(matrix?.[d]?.[h] ?? 0)
        const samp = Number(samples?.[d]?.[h] ?? 0)
        if (getActivityLevel(val, samp, quartiles) !== level) continue
        slotCount += 1
        totalPosts += samp
        engagementWeighted += val * samp
      }
    }
    return {
      level,
      slotCount,
      totalPosts,
      avgEngagement: totalPosts > 0 ? engagementWeighted / totalPosts : 0,
    }
  })
}

function computeTierSummariesGrid(matrix, samples, quartiles) {
  return [1, 2, 3].map((level) => {
    let slotCount = 0
    let totalPosts = 0
    let engagementWeighted = 0
    for (let r = 0; r < matrix.length; r += 1) {
      for (let c = 0; c < (matrix[r]?.length ?? 0); c += 1) {
        const val = Number(matrix[r]?.[c] ?? 0)
        const samp = Number(samples[r]?.[c] ?? 0)
        if (getActivityLevel(val, samp, quartiles) !== level) continue
        slotCount += 1
        totalPosts += samp
        engagementWeighted += val * samp
      }
    }
    return {
      level,
      slotCount,
      totalPosts,
      avgEngagement: totalPosts > 0 ? engagementWeighted / totalPosts : 0,
    }
  })
}

function parseISODate(iso) {
  if (!iso || typeof iso !== 'string') return null
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return null
  return new Date(y, m - 1, d)
}

function formatTooltipDate(iso) {
  const dt = parseISODate(iso)
  if (!dt) return iso
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
}

function formatAxisDate(iso) {
  const dt = parseISODate(iso)
  if (!dt) return iso
  const mo = dt.toLocaleDateString('en-US', { month: 'short' })
  const day = dt.getDate()
  return `${mo} ${day}`
}

function isWeekendIso(iso) {
  const dt = parseISODate(iso)
  if (!dt) return false
  const dow = dt.getDay()
  return dow === 0 || dow === 6
}

function formatMonthAxis(ym) {
  if (!ym || typeof ym !== 'string') return ym
  const [y, m] = ym.split('-').map(Number)
  if (!y || !m) return ym
  const dt = new Date(y, m - 1, 1)
  return dt.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
}

function formatMonthTooltip(ym) {
  if (!ym || typeof ym !== 'string') return ym
  const [y, m] = ym.split('-').map(Number)
  if (!y || !m) return ym
  const dt = new Date(y, m - 1, 1)
  return dt.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

/** Normalize API range strings to an en dash (matches Message Heatmap panel header). */
function formatOptimalPostingHeaderDateRange(label) {
  if (!label || typeof label !== 'string') return ''
  return label.replace(/\s*-\s*/g, ' – ')
}

/**
 * Optimal posting times: one panel with Daily / Weekly / Monthly views (same tier scale and signals).
 */
export default function OptimalPostingTimesUnifiedWidget({
  platformSeries,
  calendarByDay: calendarByDayProp,
  dateRangeStartKey,
  dateRangeEndKey,
  dateRangeLabel,
  footerActions,
  apiPostingHeatmap = null,
  preferApiHeatmap = false,
  propertyTimezone,
  loading = false,
}) {
  const viewMode = 'weekly'
  const [hoveredCell, setHoveredCell] = useState(null)
  const pinnedTier = null
  const plotRef = useRef(null)
  const tooltipAnchorRef = useRef(null)
  const previousViewForMotionRef = useRef(null)
  const [tooltipLayout, setTooltipLayout] = useState(null)

  const weeklyData = useMemo(() => {
    const fallbackTimezone = propertyTimezone || OPTIMAL_POSTING_PROPERTY_TIMEZONE
    if (apiPostingHeatmap?.weeklyData) {
      return {
        ...apiPostingHeatmap.weeklyData,
        timezone: propertyTimezone || apiPostingHeatmap.weeklyData.timezone || fallbackTimezone,
      }
    }
    if (preferApiHeatmap) {
      return { ...emptyWeeklyData(), timezone: fallbackTimezone }
    }
    const hasSeries = platformSeries && Array.isArray(platformSeries) && platformSeries.length > 0
    if (hasSeries) {
      return adaptPlatformSeriesToOptimalPostingTimes(platformSeries, {
        timezone: fallbackTimezone,
        metric: 'engagement',
      })
    }
    return { ...emptyWeeklyData(), timezone: fallbackTimezone }
  }, [apiPostingHeatmap, preferApiHeatmap, platformSeries, propertyTimezone])

  const monthlyData = useMemo(() => {
    if (apiPostingHeatmap?.monthlyData) {
      return apiPostingHeatmap.monthlyData
    }
    if (preferApiHeatmap) {
      return emptyMonthlyData()
    }
    if (!dateRangeStartKey || !dateRangeEndKey) {
      return {
        dates: [],
        matrix: [],
        samples: [],
        hours: [],
        totalPostsUsed: 0,
      }
    }
    return adaptPlatformSeriesToOptimalPostingMonthlyHourly(platformSeries || [], dateRangeStartKey, dateRangeEndKey)
  }, [apiPostingHeatmap, preferApiHeatmap, platformSeries, dateRangeStartKey, dateRangeEndKey])

  const calendarByDay = apiPostingHeatmap?.calendarByDay
    ?? (preferApiHeatmap && !apiPostingHeatmap ? emptyCalendarByDay() : calendarByDayProp)

  const calDates = useMemo(() => (Array.isArray(calendarByDay?.dates) ? calendarByDay.dates : []), [calendarByDay])
  const calN = calDates.length
  const hourList = useMemo(() => (Array.isArray(calendarByDay?.hours) ? calendarByDay.hours : []), [calendarByDay])
  const hourCount = hourList.length

  const dailySamples = useMemo(() => {
    const sm = calendarByDay?.samples
    if (!Array.isArray(sm) || hourCount === 0 || calN === 0) return []
    return sm
      .slice(0, hourCount)
      .map((row) => (Array.isArray(row) ? row.slice(0, calN).map((x) => Number(x) || 0) : Array(calN).fill(0)))
  }, [calendarByDay, hourCount, calN])

  const dailyMatrix = useMemo(() => {
    const vals = calendarByDay?.matrix
    if (!Array.isArray(vals) || hourCount === 0 || calN === 0) return []
    return vals
      .slice(0, hourCount)
      .map((row) => (Array.isArray(row) ? row.slice(0, calN).map((x) => Number(x) || 0) : Array(calN).fill(0)))
  }, [calendarByDay, hourCount, calN])

  const monthDates = useMemo(() => (Array.isArray(monthlyData?.dates) ? monthlyData.dates : []), [monthlyData])
  const monthN = monthDates.length
  const monthHourList = useMemo(() => (Array.isArray(monthlyData?.hours) ? monthlyData.hours : []), [monthlyData])
  const monthHourCount = monthHourList.length

  const monthSamples = useMemo(() => {
    const sm = monthlyData?.samples
    if (!Array.isArray(sm) || monthHourCount === 0 || monthN === 0) return []
    return sm
      .slice(0, monthHourCount)
      .map((row) => (Array.isArray(row) ? row.slice(0, monthN).map((x) => Number(x) || 0) : Array(monthN).fill(0)))
  }, [monthlyData, monthHourCount, monthN])

  const monthMatrix = useMemo(() => {
    const vals = monthlyData?.matrix
    if (!Array.isArray(vals) || monthHourCount === 0 || monthN === 0) return []
    return vals
      .slice(0, monthHourCount)
      .map((row) => (Array.isArray(row) ? row.slice(0, monthN).map((x) => Number(x) || 0) : Array(monthN).fill(0)))
  }, [monthlyData, monthHourCount, monthN])

  const {
    matrix: dailyMatrix24,
    samples: dailySamples24,
    engagements: dailyEngagements24,
  } = useMemo(
    () => expandTrimmedHourlyMatrixTo24(
      dailyMatrix,
      dailySamples,
      hourList,
      calN,
      calendarByDay?.engagements,
    ),
    [dailyMatrix, dailySamples, hourList, calN, calendarByDay],
  )

  const {
    matrix: monthMatrix24,
    samples: monthSamples24,
    engagements: monthEngagements24,
  } = useMemo(
    () => expandTrimmedHourlyMatrixTo24(
      monthMatrix,
      monthSamples,
      monthHourList,
      monthN,
      monthlyData?.engagements,
    ),
    [monthMatrix, monthSamples, monthHourList, monthN, monthlyData],
  )

  const dailyIntensityTotal = useMemo(
    () => dailyMatrix24.flat().reduce((sum, v) => sum + v, 0),
    [dailyMatrix24],
  )
  const monthIntensityTotal = useMemo(
    () => monthMatrix24.flat().reduce((sum, v) => sum + v, 0),
    [monthMatrix24],
  )

  const {
    matrix: wMatrix,
    samples: wSamples,
    engagements: wEngagements = null,
    totalPostsUsed: wTotalPosts,
    totalEngagements: wTotalEngagements = null,
  } = weeklyData
  const hasEngagementTotals = wTotalEngagements != null && Number.isFinite(Number(wTotalEngagements))

  const hasEnoughWeekly = wTotalPosts >= MIN_POSTS_FOR_DATA
  const hasEnoughDaily =
    (calendarByDay?.totalPostsUsed ?? 0) >= MIN_POSTS_FOR_DATA && hourCount > 0 && calN > 0
  const hasEnoughMonthly =
    (monthlyData?.totalPostsUsed ?? 0) >= MIN_POSTS_FOR_DATA && monthHourCount > 0 && monthN > 0

  const quartilesWeekly = useMemo(
    () =>
      hasEnoughWeekly
        ? getHeatmapQuartiles(wMatrix, wSamples, { minSamples: MIN_POSTS_PER_SLOT })
        : { q1: 0, q3: 0, top3Keys: [] },
    [wMatrix, wSamples, hasEnoughWeekly],
  )

  const quartilesDailyFull = useMemo(() => {
    if (!hasEnoughDaily || calN === 0) return { q1: 0, q3: 0, top3Keys: [] }
    return getHeatmapQuartilesGrid(dailyMatrix24, dailySamples24, { minSamples: MIN_POSTS_PER_SLOT })
  }, [hasEnoughDaily, calN, dailyMatrix24, dailySamples24])

  const quartilesMonthlyFull = useMemo(() => {
    if (!hasEnoughMonthly || monthN === 0) return { q1: 0, q3: 0, top3Keys: [] }
    return getHeatmapQuartilesGrid(monthMatrix24, monthSamples24, { minSamples: MIN_POSTS_PER_SLOT })
  }, [hasEnoughMonthly, monthN, monthMatrix24, monthSamples24])

  const hasEnoughActive =
    viewMode === 'weekly' ? hasEnoughWeekly : viewMode === 'daily' ? hasEnoughDaily : hasEnoughMonthly

  const opportunities = useMemo(() => {
    if (!hasEnoughActive) {
      return {
        bestPerforming: null,
        consistentPerformer: null,
        improvementOpportunity: null,
        hasAny: false,
      }
    }
    if (viewMode === 'weekly' && hasEnoughWeekly) {
      return deriveWeekdayPostingOpportunities(wMatrix, wSamples, quartilesWeekly, {
        minSamples: MIN_POSTS_PER_SLOT,
        engagements: wEngagements,
        totalEngagements: wTotalEngagements,
      })
    }
    if (viewMode === 'daily' && hasEnoughDaily) {
      const hourLabels = HOURS.map((h) => formatAxisHour(h))
      return deriveDateRangePostingOpportunities(
        dailyMatrix24,
        dailySamples24,
        quartilesDailyFull,
        calDates,
        hourLabels,
        {
          minSamples: MIN_POSTS_PER_SLOT,
          engagements: dailyEngagements24,
          totalEngagements: calendarByDay?.totalEngagements,
        },
      )
    }
    if (viewMode === 'monthly' && hasEnoughMonthly) {
      const hourLabels = HOURS.map((h) => formatAxisHour(h))
      return deriveDateRangePostingOpportunities(
        monthMatrix24,
        monthSamples24,
        quartilesMonthlyFull,
        monthDates,
        hourLabels,
        {
          minSamples: MIN_POSTS_PER_SLOT,
          engagements: monthEngagements24,
          totalEngagements: monthlyData?.totalEngagements,
        },
      )
    }
    return {
      bestPerforming: null,
      consistentPerformer: null,
      improvementOpportunity: null,
      hasAny: false,
    }
  }, [
    hasEnoughActive,
    viewMode,
    hasEnoughWeekly,
    hasEnoughDaily,
    hasEnoughMonthly,
    wMatrix,
    wSamples,
    wEngagements,
    wTotalEngagements,
    quartilesWeekly,
    dailyMatrix24,
    dailySamples24,
    dailyEngagements24,
    quartilesDailyFull,
    calDates,
    calendarByDay,
    monthMatrix24,
    monthSamples24,
    monthEngagements24,
    quartilesMonthlyFull,
    monthDates,
    monthlyData,
  ])

  const opportunityRowConfig =
    viewMode === 'weekly' ? undefined : DAILY_OPPORTUNITY_ROW_CONFIG

  const weightedInsightMatrix = useMemo(() => {
    if (viewMode === 'weekly' && hasEnoughWeekly) {
      return weeklyWeightedEngagementMatrix24(wMatrix, wSamples, DISPLAY_DAYS, MIN_POSTS_PER_SLOT)
    }
    if (viewMode === 'daily' && hasEnoughDaily) {
      return gridWeightedEngagementMatrix24(dailyMatrix24, dailySamples24, MIN_POSTS_PER_SLOT)
    }
    if (viewMode === 'monthly' && hasEnoughMonthly) {
      return gridWeightedEngagementMatrix24(monthMatrix24, monthSamples24, MIN_POSTS_PER_SLOT)
    }
    return null
  }, [
    viewMode,
    hasEnoughWeekly,
    hasEnoughDaily,
    hasEnoughMonthly,
    wMatrix,
    wSamples,
    dailyMatrix24,
    dailySamples24,
    monthMatrix24,
    monthSamples24,
  ])

  const insightWeightedTotal = useMemo(
    () =>
      weightedInsightMatrix
        ? weightedInsightMatrix.flat().reduce((s, v) => s + (Number(v) || 0), 0)
        : 0,
    [weightedInsightMatrix],
  )

  const hourTotalsReadability = useMemo(
    () => (weightedInsightMatrix ? hourTotalsFromMatrix24(weightedInsightMatrix) : Array(24).fill(0)),
    [weightedInsightMatrix],
  )

  const peakWindowReadability = useMemo(
    () => bestPeakHourWindow(hourTotalsReadability, 4),
    [hourTotalsReadability],
  )

  const showPeakHourBand = useMemo(
    () =>
      hasEnoughActive &&
      weightedInsightMatrix != null &&
      weightedInsightMatrix.length === 24 &&
      peakWindowReadability != null &&
      shouldShowPeakBand(hourTotalsReadability, peakWindowReadability, insightWeightedTotal),
    [
      hasEnoughActive,
      weightedInsightMatrix,
      peakWindowReadability,
      hourTotalsReadability,
      insightWeightedTotal,
    ],
  )

  const peakBandLayoutStyle = useMemo(() => {
    if (!showPeakHourBand || !peakWindowReadability) return null
    return peakBandStyleFromHours(peakWindowReadability.startH, peakWindowReadability.endH)
  }, [showPeakHourBand, peakWindowReadability])

  const topPeakKeySet = useMemo(
    () => (weightedInsightMatrix ? topThreePeakSlotKeys(weightedInsightMatrix) : new Set()),
    [weightedInsightMatrix],
  )

  useEffect(() => {
    setHoveredCell(null)
  }, [viewMode])

  const isHoveredWeekly = (d, h) => hoveredCell?.mode === 'weekly' && hoveredCell?.d === d && hoveredCell?.h === h

  useLayoutEffect(() => {
    if (!hoveredCell || !tooltipAnchorRef.current) {
      setTooltipLayout(null)
      return undefined
    }
    const el = tooltipAnchorRef.current
    const measure = () => {
      setTooltipLayout(measureOptimalPostingTooltipLayout(el))
    }
    measure()
    window.addEventListener('scroll', measure, true)
    window.addEventListener('resize', measure)
    return () => {
      window.removeEventListener('scroll', measure, true)
      window.removeEventListener('resize', measure)
    }
  }, [hoveredCell])

  const weeklyHoveredTier =
    hoveredCell?.mode === 'weekly' && hasEnoughWeekly
      ? getActivityLevel(
          Number(wMatrix?.[hoveredCell.d]?.[hoveredCell.h] ?? 0),
          Number(wSamples?.[hoveredCell.d]?.[hoveredCell.h] ?? 0),
          quartilesWeekly,
        )
      : 0

  const gridHoveredTier =
    hoveredCell?.mode === 'grid'
      ? getActivityLevel(
          hoveredCell.value,
          hoveredCell.sampleCount,
          viewMode === 'daily' ? quartilesDailyFull : quartilesMonthlyFull,
        )
      : 0

  const weeklySlotMetrics = useMemo(() => {
    if (hoveredCell?.mode !== 'weekly' || !hasEnoughWeekly) return null
    return getWeekdaySlotPostingTimeMetrics(wMatrix, wSamples, hoveredCell.d, hoveredCell.h, {
      minSamples: MIN_POSTS_PER_SLOT,
      engagements: wEngagements,
      totalEngagements: wTotalEngagements,
    })
  }, [hoveredCell, hasEnoughWeekly, wMatrix, wSamples, wEngagements, wTotalEngagements])

  const gridSlotMetrics = useMemo(() => {
    if (hoveredCell?.mode !== 'grid') return null
    const matrix = viewMode === 'daily' ? dailyMatrix24 : monthMatrix24
    const samples = viewMode === 'daily' ? dailySamples24 : monthSamples24
    const engagements = viewMode === 'daily' ? dailyEngagements24 : monthEngagements24
    const totalEngagements = viewMode === 'daily'
      ? calendarByDay?.totalEngagements
      : monthlyData?.totalEngagements
    return getMatrixCellPostingTimeMetrics(matrix, samples, hoveredCell.hour, hoveredCell.col, {
      minSamples: MIN_POSTS_PER_SLOT,
      engagements,
      totalEngagements,
    })
  }, [
    hoveredCell,
    viewMode,
    dailyMatrix24,
    dailySamples24,
    dailyEngagements24,
    monthEngagements24,
    calendarByDay,
    monthlyData,
    monthMatrix24,
    monthSamples24,
  ])

  const barTooltipEl =
    hoveredCell &&
    tooltipLayout &&
    createPortal(
      <div
        className="optimalPostingTimesBarTooltipAnchor"
        role="tooltip"
        style={{
          position: 'fixed',
          left: tooltipLayout.x,
          top: tooltipLayout.y,
          transform: tooltipLayout.transform,
          zIndex: 6000,
        }}
      >
        {hoveredCell.mode === 'weekly' && weeklySlotMetrics ? (
          <OptimalPostingTimesSlotTooltip
            header={formatSlotTooltipHeader(hoveredCell.d, hoveredCell.h)}
            postCount={weeklySlotMetrics.postCount}
            tierLevel={weeklyHoveredTier}
            minPostsPerSlot={MIN_POSTS_PER_SLOT}
            performanceVsPeak={weeklySlotMetrics.performanceVsPeak}
            shareOfTotalEngagement={weeklySlotMetrics.shareOfTotalEngagement}
            isHighestPerforming={weeklySlotMetrics.isHighestPerforming}
            hasValidPosts={weeklySlotMetrics.hasValidPosts}
          />
        ) : hoveredCell.mode === 'grid' && gridSlotMetrics ? (
          <OptimalPostingTimesSlotTooltip
            header={hoveredCell.titleLine}
            postCount={gridSlotMetrics.postCount}
            tierLevel={gridHoveredTier}
            minPostsPerSlot={MIN_POSTS_PER_SLOT}
            performanceVsPeak={gridSlotMetrics.performanceVsPeak}
            shareOfTotalEngagement={gridSlotMetrics.shareOfTotalEngagement}
            isHighestPerforming={gridSlotMetrics.isHighestPerforming}
            hasValidPosts={gridSlotMetrics.hasValidPosts}
          />
        ) : null}
      </div>,
      document.body,
    )

  const panelTitleMeta = useMemo(() => {
    if (!hasEnoughWeekly) return null
    const posts = formatNumber(wTotalPosts)
    const rangePart = dateRangeLabel ? formatOptimalPostingHeaderDateRange(dateRangeLabel) : ''
    const engagementPart = hasEngagementTotals
      ? ` · ${formatCompactNumber(wTotalEngagements)} engagements`
      : ''
    return `${posts} posts published${engagementPart}${rangePart ? ` · ${rangePart}` : ''}`
  }, [hasEnoughWeekly, wTotalPosts, hasEngagementTotals, wTotalEngagements, dateRangeLabel])

  const panelTitle = useMemo(
    () => (
      <OptimalPostingTimesPanelTitle
        title={OPTIMAL_POSTING_WIDGET_TITLE}
        statsLine={panelTitleMeta}
      />
    ),
    [panelTitleMeta],
  )

  const emptyMessage =
    viewMode === 'weekly'
      ? 'Not enough published-post data is available to identify notable publishing windows for the selected period.'
      : viewMode === 'daily'
        ? 'Not enough historical data for this range to show daily posting times.'
        : 'Not enough historical data for this range to show monthly posting times.'

  const actions = useMemo(() => {
    const timezone = propertyTimezone || weeklyData?.timezone
    const timezoneLabel = hasEnoughWeekly && timezone
      ? formatOptimalPostingTimezoneLabel(timezone)
      : null
    return timezoneLabel ? <span className="optimalPostingTimesTimezone">{timezoneLabel}</span> : null
  }, [hasEnoughWeekly, propertyTimezone, weeklyData?.timezone])

  const useScrollX = viewMode === 'daily' || viewMode === 'monthly'

  /** Monthly is fewer buckets — fat bars + gaps; daily stays dense for many days. */
  const chartLayoutVars = useMemo(() => {
    const base = { '--messageVolumeDailyPlotBaseHeight': '320px' }
    if (viewMode === 'monthly') {
      const { trackPx, gapPx, colWidthPx } = MONTHLY_BAR_LAYOUT
      return {
        ...base,
        '--messageVolumeDailyColTrack': `${trackPx}px`,
        '--messageVolumeDailyColWidth': `${colWidthPx}px`,
        '--messageVolumeDailyColGap': `${gapPx}px`,
      }
    }
    return {
      ...base,
      '--messageVolumeDailyColTrack': '42px',
      '--messageVolumeDailyColWidth': '15px',
      '--messageVolumeDailyColGap': '3px',
    }
  }, [viewMode])

  /** Exact row width so monthly X labels line up with bar columns (not full-width flex center). */
  const monthlyClusterStyle = useMemo(() => {
    if (viewMode !== 'monthly' || monthN < 1) return {}
    const { trackPx, gapPx } = MONTHLY_BAR_LAYOUT
    const w = monthN * trackPx + (monthN - 1) * gapPx
    return { '--optimalPostingMonthlyClusterPx': `${w}px` }
  }, [viewMode, monthN])

  const chartViewShiftAttr = useMemo(() => {
    const prev = previousViewForMotionRef.current
    if (prev === null) {
      previousViewForMotionRef.current = viewMode
      return 'none'
    }
    const nextIdx = VIEW_TAB_ORDER[viewMode] ?? 0
    const prevIdx = VIEW_TAB_ORDER[prev] ?? 0
    const delta = nextIdx - prevIdx
    previousViewForMotionRef.current = viewMode
    if (delta === 0) return 'none'
    return delta > 0 ? 'from-end' : 'from-start'
  }, [viewMode])

  const renderOptimalPostingColumns = () => {
    if (viewMode === 'weekly') {
      return DISPLAY_DAYS.map(({ label, dataIndex: d }, colIndex) => (
        <div key={label} className="optimalPostingTimesBarColumnWrap">
          <div className="optimalPostingTimesBarColumn">
            {HOURS.map((h) => {
              const val = wMatrix?.[d]?.[h] ?? 0
              const samp = wSamples?.[d]?.[h] ?? 0
              const level = getActivityLevel(val, samp, quartilesWeekly)
              const bg = getSegmentBackgroundColor(level, pinnedTier)
              const rankPeak = topPeakKeySet.has(`${h},${colIndex}`)
              return (
                <button
                  key={h}
                  type="button"
                  className={[
                    'optimalPostingTimesBarSegment',
                    isHoveredWeekly(d, h) ? 'optimalPostingTimesBarSegment--highlighted' : '',
                    rankPeak ? 'optimalPostingTimesBarSegment--rankPeak' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{ backgroundColor: bg }}
                  aria-label={`${label} ${formatAxisHour(h)}, ${formatNumber(samp)} posts, ${formatPercent(val, 1)} vs strongest slot this week`}
                  onMouseEnter={(e) => {
                    tooltipAnchorRef.current = e.currentTarget
                    setHoveredCell({ mode: 'weekly', d, h })
                  }}
                  onMouseLeave={() => {
                    tooltipAnchorRef.current = null
                    setHoveredCell(null)
                  }}
                  onFocus={(e) => {
                    tooltipAnchorRef.current = e.currentTarget
                    setHoveredCell({ mode: 'weekly', d, h })
                  }}
                  onBlur={() => {
                    tooltipAnchorRef.current = null
                    setHoveredCell(null)
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              )
            })}
          </div>
        </div>
      ))
    }
    if (viewMode === 'daily') {
      return calDates.map((iso, colIndex) => (
        <div key={`${iso}-${colIndex}`} className="optimalPostingTimesBarColumnWrap">
          <div className="optimalPostingTimesBarColumn">
            {HOURS.map((h) => {
              const value = dailyMatrix24[h]?.[colIndex] ?? 0
              const sampleCount = dailySamples24[h]?.[colIndex] ?? 0
              const level = getActivityLevel(value, sampleCount, quartilesDailyFull)
              const isHighlighted =
                hoveredCell?.mode === 'grid' && hoveredCell?.col === colIndex && hoveredCell?.hour === h
              const bg = getSegmentBackgroundColor(level, pinnedTier)
              const percent = dailyIntensityTotal ? (value / dailyIntensityTotal) * 100 : 0
              const rankPeak = topPeakKeySet.has(`${h},${colIndex}`)
              return (
                <button
                  key={`${iso}-${h}`}
                  type="button"
                  className={[
                    'optimalPostingTimesBarSegment',
                    isHighlighted ? 'optimalPostingTimesBarSegment--highlighted' : '',
                    rankPeak ? 'optimalPostingTimesBarSegment--rankPeak' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{ backgroundColor: level === 0 ? 'transparent' : bg }}
                  aria-label={`${formatTooltipDate(iso)} ${formatAxisHour(h)}, ${formatNumber(sampleCount)} posts, ${formatPercent(value / 100, 1)} of the busiest slot in these dates`}
                  onMouseEnter={(e) => {
                    tooltipAnchorRef.current = e.currentTarget
                    setHoveredCell({
                      mode: 'grid',
                      col: colIndex,
                      hour: h,
                      value,
                      percent,
                      sampleCount,
                      titleLine: `${formatTooltipDate(iso)} · ${formatAxisHour(h)}`,
                    })
                  }}
                  onMouseLeave={() => {
                    tooltipAnchorRef.current = null
                    setHoveredCell(null)
                  }}
                  onFocus={(e) => {
                    tooltipAnchorRef.current = e.currentTarget
                    setHoveredCell({
                      mode: 'grid',
                      col: colIndex,
                      hour: h,
                      value,
                      percent,
                      sampleCount,
                      titleLine: `${formatTooltipDate(iso)} · ${formatAxisHour(h)}`,
                    })
                  }}
                  onBlur={() => {
                    tooltipAnchorRef.current = null
                    setHoveredCell(null)
                  }}
                  onClick={(ev) => ev.stopPropagation()}
                />
              )
            })}
          </div>
        </div>
      ))
    }
    return monthDates.map((ym, colIndex) => (
      <div key={`${ym}-${colIndex}`} className="optimalPostingTimesBarColumnWrap">
        <div className="optimalPostingTimesBarColumn">
          {HOURS.map((h) => {
            const value = monthMatrix24[h]?.[colIndex] ?? 0
            const sampleCount = monthSamples24[h]?.[colIndex] ?? 0
            const level = getActivityLevel(value, sampleCount, quartilesMonthlyFull)
            const isHighlighted =
              hoveredCell?.mode === 'grid' && hoveredCell?.col === colIndex && hoveredCell?.hour === h
            const bg = getSegmentBackgroundColor(level, pinnedTier)
            const percent = monthIntensityTotal ? (value / monthIntensityTotal) * 100 : 0
            const rankPeak = topPeakKeySet.has(`${h},${colIndex}`)
            return (
              <button
                key={`${ym}-${h}`}
                type="button"
                className={[
                  'optimalPostingTimesBarSegment',
                  isHighlighted ? 'optimalPostingTimesBarSegment--highlighted' : '',
                  rankPeak ? 'optimalPostingTimesBarSegment--rankPeak' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{ backgroundColor: level === 0 ? 'transparent' : bg }}
                aria-label={`${formatMonthTooltip(ym)} ${formatAxisHour(h)}, ${formatNumber(sampleCount)} posts, ${formatPercent(value / 100, 1)} of the busiest slot in this range`}
                onMouseEnter={(e) => {
                  tooltipAnchorRef.current = e.currentTarget
                  setHoveredCell({
                    mode: 'grid',
                    col: colIndex,
                    hour: h,
                    value,
                    percent,
                    sampleCount,
                    titleLine: `${formatMonthTooltip(ym)} · ${formatAxisHour(h)}`,
                  })
                }}
                onMouseLeave={() => {
                  tooltipAnchorRef.current = null
                  setHoveredCell(null)
                }}
                onFocus={(e) => {
                  tooltipAnchorRef.current = e.currentTarget
                  setHoveredCell({
                    mode: 'grid',
                    col: colIndex,
                    hour: h,
                    value,
                    percent,
                    sampleCount,
                    titleLine: `${formatMonthTooltip(ym)} · ${formatAxisHour(h)}`,
                  })
                }}
                onBlur={() => {
                  tooltipAnchorRef.current = null
                  setHoveredCell(null)
                }}
                onClick={(ev) => ev.stopPropagation()}
              />
            )
          })}
        </div>
      </div>
    ))
  }

  const plotInner = (
    <>
      <div className="optimalPostingTimesBarGrid" aria-hidden="true" />
      {peakBandLayoutStyle ? (
        <div
          className="optimalPostingTimesPeakHourBand"
          style={peakBandLayoutStyle}
          aria-hidden="true"
        />
      ) : null}
      <div
        className="optimalPostingTimesBarColumns optimalPostingTimesBarColumns--barsViewMotion"
        key={viewMode}
        data-view-shift={chartViewShiftAttr}
      >
        {renderOptimalPostingColumns()}
      </div>
    </>
  )

  return (
    <ChartPanel
      dataChartId={OPTIMAL_POSTING_TIMES_UNIFIED_CHART_ID}
      title={panelTitle}
      subtitle={hasEngagementTotals ? OPTIMAL_POSTING_ENGAGEMENT_SUBTITLE : OPTIMAL_POSTING_WIDGET_SUBTITLE}
      actions={actions}
      footerActions={footerActions}
      loading={loading}
      className="optimalPostingTimesPanel messageVolumeDailyPanel messageHeatmapPanel optimalPostingTimesUnifiedPanel"
    >
      {!loading && !hasEnoughActive ? (
        <div className="optimalPostingTimesEmpty" role="status">
          <p className="optimalPostingTimesEmptyText">{emptyMessage}</p>
        </div>
      ) : (
        <>
          <div className="optimalPostingTimesMainRow optimalPostingTimesMainRow--chart">
          <div className="optimalPostingTimesBarChartWrap">
            <div
              className={`optimalPostingTimesBarChart optimalPostingTimesBarChart--unified${useScrollX ? ' optimalPostingTimesBarChart--pannable' : ''}${viewMode === 'monthly' ? ' optimalPostingTimesBarChart--monthlyWide' : ''}`}
              role="region"
              aria-label={
                viewMode === 'weekly'
                  ? 'Posts by day of week and time of day'
                  : viewMode === 'daily'
                    ? 'Posts by calendar day and time of day'
                    : 'Posts by month and time of day'
              }
              style={{ ...chartLayoutVars, ...monthlyClusterStyle }}
            >
              <div className="optimalPostingTimesBarYAxis" aria-hidden="true">
                {Y_AXIS_HOURS.map((h) => (
                  <span key={h} className="optimalPostingTimesBarYTick tabular-nums">
                    {formatAxisHour(h)}
                  </span>
                ))}
              </div>
              <div className="optimalPostingTimesBarHScroll">
                <div className="optimalPostingTimesBarHScrollInner">
                  <div className="optimalPostingTimesBarPlot" ref={plotRef}>
                    {plotInner}
                  </div>
                  <div className="optimalPostingTimesBarXAxisTrack">
                    {viewMode === 'weekly' ? (
                      <div className="optimalPostingTimesBarXAxis optimalPostingTimesBarXAxis--weeklyStrip">
                        {DISPLAY_DAYS.map(({ label, weekend }) => (
                          <span
                            key={label}
                            className={`optimalPostingTimesBarDayLabel ${weekend ? 'optimalPostingTimesBarDayLabel--weekend' : 'optimalPostingTimesBarDayLabel--weekday'}`}
                          >
                            {label}
                          </span>
                        ))}
                      </div>
                    ) : viewMode === 'daily' ? (
                      <div className="messageHeatmapBarXAxis messageHeatmapBarXAxis--dailyCalendar">
                        {calDates.map((iso, i) => (
                          <span
                            key={`x-${iso}-${i}`}
                            className={`messageHeatmapBarDayLabel messageHeatmapBarDayLabel--dailySlot ${
                              isWeekendIso(iso)
                                ? 'messageHeatmapBarDayLabel--weekend'
                                : 'messageHeatmapBarDayLabel--weekday'
                            }`}
                            title={formatTooltipDate(iso)}
                          >
                            {formatAxisDate(iso)}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <div className="messageHeatmapBarXAxis messageHeatmapBarXAxis--dailyCalendar optimalPostingTimesBarXAxis--monthlyAligned">
                        {monthDates.map((ym, i) => (
                          <span
                            key={`mx-${ym}-${i}`}
                            className="messageHeatmapBarDayLabel messageHeatmapBarDayLabel--dailySlot messageHeatmapBarDayLabel--weekday"
                            title={formatMonthTooltip(ym)}
                          >
                            {formatMonthAxis(ym)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {barTooltipEl}
            <div className="optimalPostingTimesBarLegendRow">
              <div className="campaignPerformanceLegend optimalPostingTimesBarLegend optimalPostingTimesBarLegend--compact">
                {[1, 2, 3].map((level) => {
                  const legendActive = pinnedTier === level
                  const legendMuted = pinnedTier != null && pinnedTier !== level
                  return (
                    <div
                      key={level}
                      className={`campaignPerformanceLegendItem optimalPostingTimesLegendItem ${legendActive ? 'optimalPostingTimesLegendItem--active' : ''} ${legendMuted ? 'optimalPostingTimesLegendItem--muted' : ''}`}
                    >
                      <span
                        className="campaignPerformanceLegendSwatch optimalPostingTimesLegendSwatch"
                        style={{ backgroundColor: getLegendSwatchColor(level, pinnedTier) }}
                        aria-hidden="true"
                      />
                      <span className="optimalPostingTimesLegendLabel">{TIER_LEGEND_LABELS[level]}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
          <OptimalPostingTopOpportunities
            opportunities={opportunities}
            rowConfig={opportunityRowConfig}
          />
        </div>
        </>
      )}
    </ChartPanel>
  )
}
